import { Col, FormGroup, Input, Label, Row } from 'reactstrap';
import Select from 'react-select';
import RichEditor from 'Component/Editor/RichEditor/RichEditor';

/**
 * DragpinSection (presentational)
 * - Only renders Dragpin UI (room_count, pin_count, pin->room mapping)
 * - Business logic & state remain in QuestionsForm.jsx for minimal-risk refactor
 */
export default function DragpinSection(props) {
  const {
    data,
    isUpdate,
    dragPinsRoomOptions,
    onChangeRoomCount,
    dragPinsPinsOptions,
    onChangePinCount,
    dragPinsVisibleNames,
    dragpinPinsRoom,
    dragPinsRoomOptionsBySelectedRoomCount,
    onChangePinRoom,
    dragpinPinsMeta,
    onChangePinLabel,
    onChangeRoomCustomName,
    onChangePinCustomName,
    isAnswerKeyLocked = false,
  } = props;

  // Saat soal answered: deskripsi pin boleh diedit; penempatan/room tetap dikunci.
  const lockAnswerKey = Boolean(isUpdate) || Boolean(isAnswerKeyLocked);
  const lockedAnswerKeyStyle = isAnswerKeyLocked
    ? { pointerEvents: 'none', opacity: 0.72 }
    : undefined;

  const module = {
    'toolbar': [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'header': '1' }, { 'header': '2' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['clean'],
    ],
    handlers: { 'emoji': function () { } }
  }

  const format = [
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'color', 'background',
  ];

  const toolModule = {
    toolbar: module.toolbar
  }

  return (
    <>
      <FormGroup className="mb-3">
        <Label style={{ fontWeight: 'bold' }}>{'Jumlah Room'}:</Label>
        <Select
          className="basic-single"
          classNamePrefix="select"
          placeholder="Pilih jumlah room (1 - 6)..."
          isClearable
          isSearchable={false}
          name="room_count"
          options={dragPinsRoomOptions}
          onChange={onChangeRoomCount}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => String(option.value)}
          value={
            typeof data?.room_count === 'number'
              ? { value: data.room_count, label: String(data.room_count) }
              : null
          }
          // Disable saat update / kunci jawaban terkunci
          isDisabled={lockAnswerKey}
        />
      </FormGroup>

      <FormGroup className="mb-3">
        <Label style={{ fontWeight: 'bold' }}>{'Jumlah Pins'}:</Label>
        <Select
          className="basic-single"
          classNamePrefix="select"
          placeholder="Pilih jumlah pins (1 - 10)..."
          isClearable
          isSearchable={false}
          name="pin_count"
          options={dragPinsPinsOptions}
          onChange={onChangePinCount}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => String(option.value)}
          value={
            typeof data?.pin_count === 'number'
              ? { value: data.pin_count, label: String(data.pin_count) }
              : null
          }
          // Disable saat update / kunci jawaban terkunci
          isDisabled={lockAnswerKey}
        />
      </FormGroup>

      {/* Custom room names */}
      {typeof data?.room_count === 'number' && data.room_count > 0 && (
        <FormGroup className="mb-3">
          <Label style={{ fontWeight: 'bold' }}>{'Nama Custom Room'}:</Label>
          {Array.from({ length: data.room_count }, (_, idx) => {
            const roomCustomNames = data?.interactive_detail?.room_custom_names;
            const currentValue = Array.isArray(roomCustomNames)
              ? roomCustomNames[idx] || ''
              : '';

            return (
              <div key={`room-name-${idx}`} style={{ marginBottom: 8 }}>
                <Label style={{ fontSize: '0.875rem' }}>Room {idx + 1}:</Label>
                <Input
                  type="text"
                  name={`room_custom_name_${idx}`}
                  placeholder={`Nama room ${idx + 1}...`}
                  value={currentValue}
                  onChange={(e) => onChangeRoomCustomName(idx, e.target.value)}
                  disabled={lockAnswerKey}
                />
              </div>
            );
          })}
          <small className="text-muted d-block">
            Nama room akan tampil di soal interactive. Kosongkan jika ingin pakai default (Room 1, Room 2, dst).
          </small>
        </FormGroup>
      )}

      {/* Custom pin names */}
      {typeof data?.pin_count === 'number' && data.pin_count > 0 && (
        <FormGroup className="mb-3">
          <Label style={{ fontWeight: 'bold' }}>{'Nama Custom Pin'}:</Label>
          {Array.from({ length: data.pin_count }, (_, idx) => {
            const pinCustomNames = data?.interactive_detail?.pin_custom_names;
            const currentValue = Array.isArray(pinCustomNames)
              ? pinCustomNames[idx] || ''
              : '';

            return (
              <div key={`pin-name-${idx}`} style={{ marginBottom: 8 }}>
                <Label style={{ fontSize: '0.875rem' }}>Pin {idx + 1}:</Label>
                <Input
                  type="text"
                  name={`pin_custom_name_${idx}`}
                  placeholder={`Nama pin ${idx + 1}...`}
                  value={currentValue}
                  onChange={(e) => onChangePinCustomName(idx, e.target.value)}
                  disabled={lockAnswerKey}
                />
              </div>
            );
          })}
          <small className="text-muted d-block">
            Nama pin akan tampil di soal interactive. Kosongkan jika ingin pakai default (Pin 1, Pin 2, dst).
          </small>
        </FormGroup>
      )}

      <FormGroup className="mb-3">
        <Label style={{ fontWeight: 'bold' }}>{'Deskripsi & Penempatan Pin'}:</Label>

        {/* Render mapping pin -> room (UI only). State update ditangani via onChangePinRoom */}
        {dragPinsVisibleNames.map((name, idx) => {
          const rawValue = Array.isArray(dragpinPinsRoom) ? dragpinPinsRoom[idx] : undefined;
          const pinMeta = Array.isArray(dragpinPinsMeta) ? dragpinPinsMeta[idx] : undefined;
          const pinLabelValue = typeof pinMeta?.label === 'string' ? pinMeta.label : '';

          // Ambil custom pin name kalau ada, kalau tidak pakai default name
          const pinCustomNames = data?.interactive_detail?.pin_custom_names;
          const customPinName = Array.isArray(pinCustomNames) && pinCustomNames[idx]
            ? pinCustomNames[idx].trim()
            : '';
          const displayName = customPinName || name;

          // NOTE: mapping lokal pakai '' untuk "unassigned".
          // Kalau langsung di-Number('') => 0, dan UI jadi salah tampil "0".
          const currentValue = rawValue === '' || rawValue === null || rawValue === undefined
            ? NaN
            : Number(rawValue);

          const selectedValue = Number.isInteger(currentValue) && currentValue >= 1
            ? { value: currentValue, label: String(currentValue) }
            : null;

          return (
            <Row key={`pin-room-${idx}`} className="align-items-center" style={{ marginBottom: 8 }}>
              <Col md="2">
                <div style={{ minHeight: 38, display: 'flex', alignItems: 'center', wordBreak: 'break-word' }}>
                  {displayName}
                </div>
              </Col>

              <Col md="10" className="d-flex flex-column gap-2">
                {/* Rich editor untuk deskripsi karakteristik pin — boleh diedit meski kunci terkunci */}
                <RichEditor
                  index={idx}
                  handleChangeDescription={onChangePinLabel}
                  description={pinLabelValue}
                  modules={toolModule}
                  formats={format}
                />
                <div style={lockedAnswerKeyStyle} aria-disabled={isAnswerKeyLocked || undefined}>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Pilih room..."
                    isClearable
                    isSearchable={false}
                    name={`pin_room_${idx}`}
                    options={dragPinsRoomOptionsBySelectedRoomCount}
                    onChange={(opt) => onChangePinRoom(idx, opt)}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => String(option.value)}
                    value={selectedValue}
                    // Disable saat update / kunci terkunci / belum ada room
                    isDisabled={lockAnswerKey || dragPinsRoomOptionsBySelectedRoomCount.length === 0}
                  />
                </div>
              </Col>
            </Row>
          );
        })}

        <small className="text-muted d-block">
          Room yang tersedia mengikuti nilai <b>Jumlah Room</b>. Nama pins tampil sesuai <b>Nama Custom Pin</b> (atau default jika kosong).
        </small>
      </FormGroup>
    </>
  );
}